import { useState, useEffect, useCallback, useRef } from 'react';
import { getOnboardingState, saveOnboardingState } from 'services/api/ClientHandler';

/**
 * Hook pour gérer l'état du tutoriel d'onboarding
 * Stocke les métriques utilisateur : qui a vu, quand, progression, temps passé
 *
 * TUTORIAL_VERSION : hasSeenTutorial() exige que la version stockée corresponde à
 * celle-ci. Changer ce numéro fait donc RÉAPPARAÎTRE le tutoriel à la prochaine
 * connexion, pour tous les utilisateurs — y compris ceux qui l'avaient déjà terminé.
 * C'est voulu pour la V3 : le contenu décrit de nouvelles pages, tout le monde doit
 * le revoir. Ne l'incrémenter qu'à l'occasion d'une vraie refonte du contenu.
 *   1.0 — tutoriel dashboard V2 (4 pages)
 *   2.0 — tutoriel dashboard V3, adapté au rôle de l'utilisateur
 */
const TUTORIAL_VERSION = '2.0';

const useOnboarding = (user) => {
    const [onboardingState, setOnboardingState] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const startTimeRef = useRef(null);
    const latestStateRef = useRef(null);
    const fetchedUserIdRef = useRef(null);

    // Ref miroir toujours synchronisé avec le dernier état commité
    useEffect(() => {
        latestStateRef.current = onboardingState;
    }, [onboardingState]);

    // Charger l'état depuis le backend au mount
    useEffect(() => {
        if (!user?.id || !user?.token) {
            setIsLoading(false);
            return;
        }

        // Une seule requête par utilisateur : évite les doublons dus au
        // double-invoke des effets en dev (React.StrictMode). Ne pas toucher
        // isLoading ici : le fetch réel (autre invocation) est en cours et
        // mettra isLoading à false lui-même à sa résolution.
        if (fetchedUserIdRef.current === user.id) {
            return;
        }
        fetchedUserIdRef.current = user.id;

        const loadOnboardingState = async () => {
            setIsLoading(true);
            try {
                const response = await getOnboardingState(user.id, user.token);
                if (response.status === 200) {
                    setOnboardingState(response.results);
                } else {
                    setOnboardingState({ users: {} });
                }
            } catch (error) {
                console.error('[useOnboarding] Error loading:', error);
                fetchedUserIdRef.current = null;
                setOnboardingState({ users: {} });
            } finally {
                setIsLoading(false);
            }
        };

        loadOnboardingState();
    }, [user?.id, user?.token]);

    // Vérifier si l'utilisateur a déjà vu le tutoriel
    const hasSeenTutorial = useCallback(() => {
        if (!onboardingState || !user?.email) return false;
        const userState = onboardingState.users?.[user.email];
        return userState?.hasCompleted && userState?.version === TUTORIAL_VERSION;
    }, [onboardingState, user?.email]);

    // Initialiser le timer au démarrage du tutoriel
    const startTimer = useCallback(() => {
        startTimeRef.current = Date.now();
    }, []);

    // Calculer le temps écoulé depuis le démarrage
    const getTimeSpent = useCallback(() => {
        if (!startTimeRef.current) return 0;
        return Math.floor((Date.now() - startTimeRef.current) / 1000); // en secondes
    }, []);

    // Tracker la vue d'une slide (mise à jour locale uniquement, pas de sauvegarde backend)
    const trackSlideView = useCallback((slideIndex) => {
        if (!user?.email) return;

        setOnboardingState(prevState => {
            if (!prevState) return prevState;

            const currentUserState = prevState.users?.[user.email] || {
                hasCompleted: false,
                lastViewedAt: new Date().toISOString(),
                totalViews: 0,
                slidesViewed: [],
                timeSpent: 0,
                version: TUTORIAL_VERSION
            };

            // Ne mettre à jour que si la slide n'était pas déjà vue
            if (currentUserState.slidesViewed.includes(slideIndex)) {
                return prevState; // Pas de changement
            }

            const updatedUserState = {
                ...currentUserState,
                slidesViewed: [...currentUserState.slidesViewed, slideIndex]
            };

            return {
                ...prevState,
                users: {
                    ...prevState.users,
                    [user.email]: updatedUserState
                }
            };
        });
    }, [user?.email]);

    // Sauvegarder l'état actuel à la fermeture du tutoriel
    const saveOnClose = useCallback(async (completed = false) => {
        if (!user?.id || !user?.token || !user?.email) return;
        const snapshot = latestStateRef.current;
        if (!snapshot) return;

        const timeSpent = getTimeSpent();

        const currentUserState = snapshot.users?.[user.email] || {
            totalViews: 0,
            slidesViewed: [],
            timeSpent: 0
        };

        const updatedState = {
            ...snapshot,
            users: {
                ...snapshot.users,
                [user.email]: {
                    ...currentUserState,
                    hasCompleted: completed || currentUserState.hasCompleted || false,
                    lastViewedAt: new Date().toISOString(),
                    timeSpent: timeSpent,
                    version: TUTORIAL_VERSION
                }
            }
        };

        setOnboardingState(updatedState);

        setIsSaving(true);
        saveOnboardingState(user.id, user.token, updatedState)
            .then(() => setIsSaving(false))
            .catch(error => {
                console.error('[useOnboarding] Error saving:', error);
                setIsSaving(false);
            });
    }, [user, getTimeSpent]);

    // Marquer le tutoriel comme complété
    const markAsCompleted = useCallback(async () => {
        await saveOnClose(true);
    }, [saveOnClose]);

    // Incrémenter le nombre de vues lors de l'ouverture du tutoriel (mise à jour locale uniquement)
    const incrementViews = useCallback(() => {
        if (!user?.email) return;

        setOnboardingState(prevState => {
            if (!prevState) return prevState;

            const currentUserState = prevState.users?.[user.email] || {
                hasCompleted: false,
                lastViewedAt: new Date().toISOString(),
                totalViews: 0,
                slidesViewed: [],
                timeSpent: 0,
                version: TUTORIAL_VERSION
            };

            const updatedUserState = {
                ...currentUserState,
                totalViews: (currentUserState.totalViews || 0) + 1
            };

            return {
                ...prevState,
                users: {
                    ...prevState.users,
                    [user.email]: updatedUserState
                }
            };
        });
    }, [user?.email]);

    // Réinitialiser le tutoriel (pour debug ou nouvelle version)
    const resetTutorial = useCallback(async () => {
        if (!user?.id || !user?.token || !user?.email || !onboardingState) return;

        const updatedState = {
            ...onboardingState,
            users: {
                ...onboardingState.users,
                [user.email]: {
                    hasCompleted: false,
                    lastViewedAt: new Date().toISOString(),
                    totalViews: 0,
                    slidesViewed: [],
                    timeSpent: 0,
                    version: TUTORIAL_VERSION
                }
            }
        };

        setOnboardingState(updatedState);

        setIsSaving(true);
        try {
            await saveOnboardingState(user.id, user.token, updatedState);
            setIsSaving(false);
        } catch (error) {
            console.error('[useOnboarding] Error resetting:', error);
            setIsSaving(false);
        }
    }, [onboardingState, user]);

    return {
        hasSeenTutorial: hasSeenTutorial(),
        markAsCompleted,
        saveOnClose,
        trackSlideView,
        startTimer,
        incrementViews,
        resetTutorial,
        isLoading,
        isSaving,
        onboardingState
    };
};

export { useOnboarding };
